import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Star, Check, Quote } from "lucide-react";
import { hero, stats, services, values, clientLogos, company } from "../mock";
import Reveal from "../components/Reveal";
import GoogleReviews from "../components/GoogleReviews";
import CountUp from "../components/CountUp";
import StickyProcess from "../components/StickyProcess";
import { ScrollScale, Parallax } from "../components/ScrollFX";
import Seo from "../components/Seo";

const Home = () => {
  return (
    <div className="overflow-hidden">
      <Seo
        title="iClean NW | Commercial Cleaning in Manchester"
        description="iClean NW is your hassle-free cleaning partner across Manchester and the North West. We handle commercial, office, industrial, contract, end-of-tenancy and grounds maintenance with a vetted, fully-trained team. Get a free quote today."
        path="/"
      />
      {/* HERO */}
      <section className="relative pt-32 lg:pt-36 pb-16">
        <div className="absolute top-24 right-0 w-[40vw] h-[40vw] max-w-[520px] max-h-[520px] rounded-full bg-[#ea731d]/10 blur-3xl -z-10" />
        <div className="max-w-6xl mx-auto px-5 lg:px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 bg-white border border-neutral-200 rounded-full px-4 py-1.5 text-xs font-semibold text-[#1a1714] shadow-sm">
              <span className="w-2 h-2 rounded-full bg-[#ea731d]" /> {hero.badge}
            </span>
            <h1 className="mt-6 text-[2.6rem] sm:text-6xl lg:text-[4.1rem] font-bold leading-[1.02] text-[#1a1714]">
              {hero.title}<br />
              <span className="text-[#ea731d]">{hero.titleAccent}</span>
            </h1>
            <p className="mt-6 text-lg text-neutral-600 leading-relaxed max-w-lg">{hero.subtitle}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-[#ea731d] hover:bg-[#d9640f] text-white font-semibold px-7 py-4 rounded-full transition-all duration-300 hover:-translate-y-0.5 brand-shadow">
                Get a free quote <ArrowRight size={19} />
              </Link>
              <Link to="/services" className="inline-flex items-center justify-center gap-2 bg-white border border-neutral-200 text-[#1a1714] font-semibold px-7 py-4 rounded-full transition-all duration-300 hover:border-[#ea731d]/50 hover:-translate-y-0.5">
                Explore services
              </Link>
            </div>
            <div className="mt-9 flex items-center gap-4">
              <div className="flex -space-x-3">
                {[0,1,2,3].map((i) => (
                  <div key={i} className="w-10 h-10 rounded-full border-2 border-white bg-gradient-to-br from-[#ffd9b8] to-[#ea731d] flex items-center justify-center text-white text-xs font-bold">
                    {["P","M","G","B"][i]}
                  </div>
                ))}
              </div>
              <div>
                <div className="flex gap-0.5">{[...Array(5)].map((_,i)=>(<Star key={i} size={15} className="text-[#ea731d] fill-[#ea731d]" />))}</div>
                <p className="text-sm text-neutral-500 mt-0.5">Trusted by 200+ North West businesses</p>
              </div>
            </div>
          </div>

          {/* Image cluster */}
          <div className="relative">
            {/* decorative layered accents (parallax) */}
            <Parallax speed={0.06} className="absolute -inset-3 -z-10 rounded-[38px] bg-gradient-to-br from-[#ea731d]/18 via-[#ea731d]/5 to-transparent" />
            <Parallax speed={0.16} className="absolute -top-6 -left-6 w-28 h-28 rounded-3xl border border-[#ea731d]/30 -z-10 hidden sm:block" />
            <Parallax speed={-0.1} className="absolute -bottom-8 right-10 w-40 h-40 rounded-full bg-[#ea731d]/10 blur-2xl -z-10" />
            <Parallax speed={0.05} className="relative rounded-[32px] overflow-hidden soft-shadow ring-1 ring-black/5">
              <img src={hero.image} alt="iClean professional cleaning team" className="w-full h-[420px] lg:h-[520px] object-cover" />
              <div className="absolute inset-0 ring-1 ring-inset ring-white/10 rounded-[32px]" />
            </Parallax>
            <div className="absolute -bottom-6 -left-4 sm:-left-8 bg-white/95 backdrop-blur rounded-2xl p-4 shadow-xl w-52 animate-float">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-[#ea731d]/12 flex items-center justify-center">
                  <Check className="text-[#ea731d]" size={22} strokeWidth={3} />
                </div>
                <div>
                  <div className="font-display font-bold text-lg text-[#1a1714] leading-none">100%</div>
                  <div className="text-xs text-neutral-500 mt-1">Vetted & trained</div>
                </div>
              </div>
            </div>
            <div className="absolute -top-4 -right-2 sm:-right-6 bg-white/95 backdrop-blur rounded-2xl px-4 py-3 shadow-xl hidden sm:flex items-center gap-2 animate-float" style={{ animationDelay: "1.2s" }}>
              <span className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse" />
              <span className="text-sm font-semibold text-[#1a1714]">Fully insured</span>
            </div>
          </div>
        </div>
      </section>

      {/* CLIENTS MARQUEE */}
      <section className="py-8">
        <p className="text-center text-xs font-semibold tracking-[0.2em] text-neutral-400 uppercase">Trusted by businesses across the North West</p>

        {/* Mobile: two rows scrolling in opposite directions */}
        <div className="lg:hidden mt-6 space-y-3">
          <div className="marquee-track marquee-fade overflow-hidden">
            <div className="flex gap-3 w-max animate-marquee">
              {[...clientLogos.slice(0, 8), ...clientLogos.slice(0, 8)].map((c, i) => (
                <div
                  key={`m1-${i}`}
                  className="flex-shrink-0 w-32 h-20 rounded-2xl border border-neutral-200/70 shadow-sm flex items-center justify-center p-3 overflow-hidden"
                  style={{ background: c.bg }}
                  title={c.name}
                >
                  <img src={c.src} alt={c.name} className="max-h-full max-w-full object-contain" loading="lazy" />
                </div>
              ))}
            </div>
          </div>
          <div className="marquee-track marquee-fade overflow-hidden">
            <div className="flex gap-3 w-max animate-marquee-reverse">
              {[...clientLogos.slice(8), ...clientLogos.slice(8)].map((c, i) => (
                <div
                  key={`m2-${i}`}
                  className="flex-shrink-0 w-32 h-20 rounded-2xl border border-neutral-200/70 shadow-sm flex items-center justify-center p-3 overflow-hidden"
                  style={{ background: c.bg }}
                  title={c.name}
                >
                  <img src={c.src} alt={c.name} className="max-h-full max-w-full object-contain" loading="lazy" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Desktop: single row */}
        <div className="hidden lg:block mt-6 marquee-track marquee-fade overflow-hidden">
          <div className="flex gap-4 w-max animate-marquee">
            {[...clientLogos, ...clientLogos].map((c, i) => (
              <div
                key={i}
                className="flex-shrink-0 w-40 h-24 rounded-2xl border border-neutral-200/70 shadow-sm flex items-center justify-center p-4 overflow-hidden"
                style={{ background: c.bg }}
                title={c.name}
              >
                <img src={c.src} alt={c.name} className="max-h-full max-w-full object-contain" loading="lazy" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="py-16 lg:py-24">
        <div className="max-w-6xl mx-auto px-5 lg:px-6">
          <Reveal className="max-w-2xl">
            <span className="text-[#ea731d] font-semibold tracking-widest text-xs uppercase">What we do</span>
            <h2 className="mt-3 text-3xl lg:text-5xl font-bold text-[#1a1714]">Cleaning services, tailored to you</h2>
            <p className="mt-4 text-neutral-600 text-lg">From a small one-off job to a long-term contract, whatever your space, we’ve got it covered.</p>
          </Reveal>

          <div className="mt-12 grid md:grid-cols-2 gap-5">
            {services.map((s, i) => {
              const Icon = s.icon;
              const large = i === 0 || i === 3;
              return (
                <Reveal key={s.id} delay={(i % 2) * 90} className={large ? "md:col-span-1" : "md:col-span-1"}>
                  <Link to="/services" className="group block relative rounded-[26px] overflow-hidden h-72 soft-shadow">
                    <img src={s.image} alt={s.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1a1714]/90 via-[#1a1714]/30 to-transparent" />
                    <div className="absolute top-5 left-5">
                      <span className="inline-flex items-center gap-2 bg-white/90 backdrop-blur text-[#1a1714] text-xs font-semibold px-3 py-1.5 rounded-full">
                        <Icon size={14} className="text-[#ea731d]" /> {s.tag}
                      </span>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <h3 className="text-xl font-bold text-white font-display">{s.title}</h3>
                      <p className="mt-1.5 text-sm text-neutral-200 line-clamp-2 max-w-md">{s.short}</p>
                      <span className="mt-3 inline-flex items-center gap-1.5 text-white font-semibold text-sm group-hover:gap-3 transition-all">
                        Learn more <ArrowRight size={16} />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SCROLL SHOWCASE (Apple-style expanding reveal) */}
      <section className="py-14 lg:py-20 overflow-hidden">
        <div className="max-w-6xl mx-auto px-5 lg:px-6 text-center mb-10">
          <Reveal>
            <span className="text-[#ea731d] font-semibold tracking-widest text-xs uppercase">The iClean standard</span>
            <h2 className="mt-3 text-3xl lg:text-5xl font-bold text-[#1a1714]">Spotless results, every single visit</h2>
          </Reveal>
        </div>
        <ScrollScale className="max-w-6xl mx-auto overflow-hidden soft-shadow" from={0.9}>
          <div className="relative">
            <img src="/generated/back.jpg?v=4" alt="The iClean team" className="w-full h-[280px] sm:h-[400px] lg:h-[560px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1a1714]/70 via-[#1a1714]/10 to-transparent" />
            <div className="absolute bottom-7 left-7 right-7 lg:bottom-10 lg:left-10">
              <h3 className="text-white font-display font-bold text-xl sm:text-3xl lg:text-4xl max-w-2xl leading-tight">
                A team you can trust, vetted, trained &amp; fully insured
              </h3>
              <p className="mt-3 text-neutral-200 max-w-xl hidden sm:block">Recognisable in our branded uniforms and committed to the highest standards on every job.</p>
            </div>
          </div>
        </ScrollScale>
      </section>

      {/* WHY / VALUES */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-5 lg:px-6 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal className="relative">
            <div className="rounded-[28px] overflow-hidden soft-shadow">
              <img src="/generated/team.jpg?v=4" alt="iClean team" className="w-full h-[460px] object-cover" />
            </div>
            <div className="absolute -bottom-6 right-6 bg-[#ea731d] text-white rounded-2xl px-6 py-5 shadow-xl">
              <div className="font-display text-3xl font-bold">7+</div>
              <div className="text-sm text-orange-50">Years of experience</div>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <span className="text-[#ea731d] font-semibold tracking-widest text-xs uppercase">Why iClean</span>
            <h2 className="mt-3 text-3xl lg:text-4xl font-bold text-[#1a1714]">The cleaning partner businesses rely on</h2>
            <p className="mt-4 text-neutral-600">We deliver and maintain the highest standards in cleaning, with total commitment to customer satisfaction through our responsiveness.</p>
            <div className="mt-8 grid sm:grid-cols-2 gap-5">
              {values.map((v) => {
                const Icon = v.icon;
                return (
                  <div key={v.title} className="flex gap-4">
                    <span className="flex-shrink-0 w-11 h-11 rounded-xl bg-[#ea731d]/12 text-[#ea731d] flex items-center justify-center"><Icon size={21} /></span>
                    <div>
                      <h3 className="font-bold text-[#1a1714]">{v.title}</h3>
                      <p className="text-sm text-neutral-600 mt-1 leading-relaxed">{v.text}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </Reveal>
        </div>
      </section>

      {/* PROCESS (Apple-style sticky storytelling) */}
      <StickyProcess />

      {/* STATS BAND */}
      <section className="py-6">
        <div className="max-w-6xl mx-auto px-5 lg:px-6">
          <div className="rounded-[28px] bg-[#1a1714] px-8 py-12 grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((s, i) => (
              <Reveal key={s.label} delay={i * 80} className="text-center">
                <div className="font-display text-4xl lg:text-5xl font-bold text-[#ea731d]"><CountUp value={s.value} /></div>
                <div className="mt-2 text-sm text-neutral-400">{s.label}</div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* GOOGLE REVIEWS */}
      <GoogleReviews />

      {/* CTA */}
      <section className="pb-24">
        <div className="max-w-6xl mx-auto px-5 lg:px-6">
          <Reveal className="relative rounded-[32px] bg-[#ea731d] overflow-hidden px-8 py-16 lg:p-20 text-center">
            <div className="absolute inset-0 dotted-bg opacity-40" />
            <div className="relative">
              <h2 className="text-3xl lg:text-5xl font-bold text-white">Ready for a spotless space?</h2>
              <p className="mt-4 text-orange-50 text-lg max-w-xl mx-auto">Whether it’s a one-off clean or a long-term contract, let’s make your premises shine.</p>
              <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-white text-[#ea731d] font-bold px-8 py-4 rounded-full hover:-translate-y-0.5 transition-transform">
                  Get a free quote <ArrowRight size={19} />
                </Link>
                <a href={company.phoneHref} className="inline-flex items-center justify-center gap-2 bg-[#1a1714] text-white font-bold px-8 py-4 rounded-full hover:-translate-y-0.5 transition-transform">
                  Call {company.phone}
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
